import React, { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon, CalendarDaysIcon } from "@heroicons/react/20/solid";
import { dayKey, mesOcupacion } from "../utils/ocupacion";

const ACCENT = "#1b4d2e";

// Modal custom (no el Dialog de Catalyst) con el calendario del mes: días
// ocupados en verde de marca, hoy con anillo, y los días que el filtro
// excluye atenuados. Se abre al hacer clic en una barra de Ocupación.
const DOW_HEADERS = ["L", "M", "M", "J", "V", "S", "D"];

const isExcluded = (dow, filtro) => {
  if (filtro === "finde") return !(dow === 5 || dow === 6 || dow === 0);
  if (filtro === "semana") return !(dow >= 1 && dow <= 4);
  return false;
};

export default function OcupacionMesDialog({ open, onClose, mes, occupiedSet, filtro }) {
  const navigate = useNavigate();
  if (!mes) return null;

  const { year, month } = mes;
  const { ocupados, total, unidad } = mesOcupacion(occupiedSet, year, month, filtro);
  const pct = total > 0 ? Math.round((ocupados / total) * 100) : 0;

  const monthName = new Date(year, month, 15).toLocaleDateString("es-MX", { month: "long" });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7; // lunes-first
  const todayKey = dayKey(new Date());

  const cells = [
    ...Array.from({ length: firstOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95 translate-y-2"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-lg font-bold text-zinc-900 capitalize">
                    {monthName} {year}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {ocupados} de {total} {unidad} con evento
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl font-bold" style={{ color: ACCENT }}>
                    {pct}%
                  </span>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                    aria-label="Cerrar"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Progress */}
              <div className="mt-3 h-1.5 w-full rounded-full overflow-hidden" style={{ backgroundColor: "#d4e3da" }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: ACCENT }}
                />
              </div>

              {/* Calendario */}
              <div className="mt-5 grid grid-cols-7 gap-1 text-center">
                {DOW_HEADERS.map((h, i) => (
                  <div key={`${h}-${i}`} className="text-[11px] font-semibold uppercase text-zinc-400 pb-1">
                    {h}
                  </div>
                ))}
                {cells.map((day, idx) => {
                  if (day == null) return <div key={`empty-${idx}`} />;
                  const date = new Date(year, month, day);
                  const k = dayKey(date);
                  const occupied = occupiedSet.has(k);
                  const excluded = isExcluded(date.getDay(), filtro);
                  const isToday = k === todayKey;
                  return (
                    <div
                      key={k}
                      className={`flex h-9 items-center justify-center rounded-lg text-sm transition ${
                        occupied ? "font-semibold text-white shadow-sm" : "text-zinc-700"
                      } ${excluded ? "opacity-30" : ""} ${
                        isToday && !occupied ? "ring-2 ring-[#1b4d2e]/40 font-semibold" : ""
                      }`}
                      style={occupied ? { backgroundColor: ACCENT } : undefined}
                      title={occupied ? "Con evento" : undefined}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-zinc-500">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ACCENT }} />
                    Con evento
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                    Libre
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/calendario")}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                  style={{ backgroundColor: ACCENT }}
                >
                  <CalendarDaysIcon className="h-3.5 w-3.5" />
                  Abrir calendario
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
